import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Clock, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type ResourceProfile, type ResourceShiftAssignment } from '@/api/resourceCenterApi'
import { useShiftDays, useShiftDefinitions } from '@/hooks/useResourceCenter'
import { shiftSummary } from '@/lib/assignmentGroups'
import { addDays, dayHeader, todayIso, weekdayOf, windowDates, windowLabel, sundayOf } from '@/lib/resourceCalendar'
import { Empty, ErrorLine, SectionCard, longDate, useProfileWrite } from './shared'
import { ShiftAssignmentDialog } from './ShiftAssignmentDialog'

const OFF = '__off__'

/**
 * Shift tab (slides 10 and 11): the current assignment (and, for Variable,
 * its day editor), the full history, and a "+ New shift" button that opens
 * the Fixed / Pattern / Variable form in a dialog.
 */
export function ShiftSection({ profile, canManage }: { profile: ResourceProfile; canManage: boolean }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const current = profile.shift
  const write = useProfileWrite()
  const summary = shiftSummary(current)
  const history = [...profile.shiftAssignments].sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))

  return (
    <SectionCard
      icon={Clock}
      title='Shift'
      hint={summary ? `${summary.title} · ${summary.detail}` : 'No shift yet'}
      action={
        canManage && (
          <Button size='sm' className='h-8' onClick={() => setDialogOpen(true)}>
            <Plus className='mr-1 h-3.5 w-3.5' /> New shift
          </Button>
        )
      }
    >
      {current?.type === 'VARIABLE' && <VariableDays profile={profile} assignment={current} canManage={canManage} />}

      {history.length === 0 ? (
        <Empty>No shift assigned — the calendar shows "—" until one starts.</Empty>
      ) : (
        <ul className='divide-y rounded-lg border text-xs'>
          {history.map((a, i) => {
            const s = shiftSummary(a)
            return (
              <li key={a.id} className='flex items-center gap-2 px-2.5 py-1.5'>
                <span className='w-40 shrink-0 text-muted-foreground'>
                  {longDate(a.effectiveFrom)} – {a.effectiveTo ? longDate(a.effectiveTo) : 'on'}
                </span>
                <span className='min-w-0 flex-1 truncate'>
                  {s?.title} <span className='text-muted-foreground'>· {s?.detail}</span>
                </span>
                {canManage && i === 0 && (
                  <button
                    type='button'
                    aria-label='Remove the latest shift assignment'
                    title='Remove — the one before runs on'
                    className='text-muted-foreground/60 hover:text-destructive'
                    onClick={() => write.run(() => resourceProfilesApi.removeShiftAssignment(profile.userId, a.id))}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <ErrorLine message={write.error} />
      {/* Mounted only while open, so each opening starts from a clean form. */}
      {canManage && dialogOpen && <ShiftAssignmentDialog profile={profile} open onOpenChange={setDialogOpen} />}
    </SectionCard>
  )
}

/** Slide 10: Variable — two weeks at a time, a shift or OFF per date. */
function VariableDays({
  profile,
  assignment,
  canManage,
}: {
  profile: ResourceProfile
  assignment: ResourceShiftAssignment
  canManage: boolean
}) {
  const shifts = useShiftDefinitions()
  const [start, setStart] = useState(() => sundayOf(todayIso() > assignment.effectiveFrom ? todayIso() : assignment.effectiveFrom))
  const dates = windowDates(start)
  const saved = useShiftDays(profile.userId, dates[0], dates[13], true)
  const [draft, setDraft] = useState<Record<string, string | null>>({})
  const write = useProfileWrite()

  useEffect(() => setDraft({}), [start])

  const savedOf = (d: string) => saved.data?.find(x => x.date === d)?.shiftId ?? null
  const valueOf = (d: string) => (d in draft ? draft[d] : savedOf(d))
  const changed = Object.entries(draft).filter(([d, v]) => v !== savedOf(d))
  const activeShifts = (shifts.data ?? []).filter(s => s.active)
  const inRange = (d: string) => d >= assignment.effectiveFrom && (!assignment.effectiveTo || d <= assignment.effectiveTo)

  return (
    <div className='space-y-2 rounded-xl border p-3'>
      <div className='flex items-center justify-between'>
        <span className='text-xs font-medium'>Days · {windowLabel(start)}</span>
        <div className='flex items-center gap-1'>
          <Button size='icon' variant='ghost' className='h-7 w-7' aria-label='Previous two weeks' onClick={() => setStart(addDays(start, -14))}>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Button size='icon' variant='ghost' className='h-7 w-7' aria-label='Next two weeks' onClick={() => setStart(addDays(start, 14))}>
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>
      <div className='grid grid-cols-7 gap-1'>
        {dates.map(d => {
          const h = dayHeader(d)
          const v = valueOf(d)
          return (
            <div key={d} className={cn('space-y-1 rounded-md p-1 text-center', h.weekend && 'bg-muted/40')}>
              <div className='text-[10px] leading-tight text-muted-foreground'>
                {h.dow}
                <br />
                {h.md}
              </div>
              <Select
                value={v ?? OFF}
                disabled={!canManage || !inRange(d)}
                onValueChange={x => setDraft({ ...draft, [d]: x === OFF ? null : x })}
              >
                <SelectTrigger
                  aria-label={`Shift on ${d}`}
                  className={cn('h-7 justify-center px-1 text-xs [&>svg]:hidden', v ? 'font-semibold' : 'text-muted-foreground', d in draft && 'ring-1 ring-amber-400')}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OFF}>OFF</SelectItem>
                  {activeShifts
                    // Only shifts worked on this date's weekday, plus the one it already has.
                    .filter(s => s.weekdays.includes(weekdayOf(d)) || s.id === v)
                    .map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.code} · {s.startTime}–{s.endTime}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )
        })}
      </div>
      {canManage && changed.length > 0 && (
        <div className='flex justify-end gap-1.5'>
          <Button size='sm' variant='ghost' className='h-7' onClick={() => setDraft({})}>
            Undo
          </Button>
          <Button
            size='sm'
            className='h-7'
            disabled={write.pending}
            onClick={() =>
              write.run(
                () => resourceProfilesApi.setShiftDays(profile.userId, changed.map(([date, shiftId]) => ({ date, shiftId }))),
                () => {
                  setDraft({})
                  void saved.refetch()
                },
              )
            }
          >
            Save {changed.length} day{changed.length === 1 ? '' : 's'}
          </Button>
        </div>
      )}
      <ErrorLine message={write.error} />
    </div>
  )
}
